import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROLES, hasPermission } from '@config/roles'
import { useDashboardStore } from '@store'
import {
	StatCard,
	ActivityFeed,
	TaskList,
	ProjectCard,
	DeadlineList,
	TeamPerformance,
	QuickActions
} from '@components/features/dashboard/widgets'
import {
	DonutChart
} from '@components/features/dashboard/charts'
import {
	DashboardGrid,
	WidgetContainer,
	StatsRow
} from '@components/features/dashboard/layouts'
import './Dashboard.scss'

/**
 * Company Admin Dashboard Page
 *
 * Displays company-wide metrics, projects, tasks, and compliance data.
 * Uses the dashboard store for data fetching and state management.
 *
 * @param {Object} props
 * @param {string} props.userRole - Current user's role for conditional rendering
 */
const Dashboard = ({ userRole = ROLES.COMPANY_ADMIN }) => {
	const navigate = useNavigate()

	// Role-based visibility helpers
	const canViewTeamPerformance = hasPermission(userRole, 'canManageCompanyUsers') ||
		hasPermission(userRole, 'canAssignTasks')
	const canManageProjects = hasPermission(userRole, 'canManageProjects') ||
		hasPermission(userRole, 'canManageAssignedProjects')

	// Dashboard store
	const {
		stats,
		projects,
		activity,
		deadlines,
		teamPerformance,
		taskSummary,
		loading,
		error,
		autoRefreshEnabled,
		fetchCompanyDashboard,
		fetchTeamPerformance,
		startAutoRefresh,
		stopAutoRefresh,
		getTimeSinceUpdate
	} = useDashboardStore()

	// Fetch dashboard data on mount
	useEffect(() => {
		fetchCompanyDashboard()

		if (canViewTeamPerformance) {
			fetchTeamPerformance()
		}

		// Start auto-refresh every 60 seconds
		const cleanup = startAutoRefresh('company-admin', 60000)

		return () => {
			cleanup()
			stopAutoRefresh()
		}
	}, [fetchCompanyDashboard, fetchTeamPerformance, canViewTeamPerformance, startAutoRefresh, stopAutoRefresh])

	// Manual refresh handler
	const handleRefresh = useCallback(() => {
		fetchCompanyDashboard()
		if (canViewTeamPerformance) {
			fetchTeamPerformance()
		}
	}, [fetchCompanyDashboard, fetchTeamPerformance, canViewTeamPerformance])

	// Quick action handlers
	const quickActions = [
		...(canManageProjects ? [{
			icon: '📁',
			label: 'New Project',
			onClick: () => navigate('/projects/new'),
			permission: 'canManageProjects'
		}] : []),
		{
			icon: '📝',
			label: 'Create Task',
			onClick: () => navigate('/tasks/new'),
			permission: 'canCreateTasks'
		},
		{
			icon: '📄',
			label: 'Upload Document',
			onClick: () => navigate('/documents/upload'),
			permission: 'canUploadDocuments'
		},
		{
			icon: '📅',
			label: 'View Calendar',
			onClick: () => navigate('/calendar'),
			permission: null
		},
		{
			icon: '📊',
			label: 'View Reports',
			onClick: () => navigate('/reports'),
			permission: 'canViewReports'
		}
	]

	// Format activity data for ActivityFeed
	const formattedActivity = (activity || []).map(item => ({
		id: item.id,
		user_name: item.user_name || 'Unknown User',
		user_initials: item.user_initials || 'U',
		action: item.action || item.type,
		target: item.target || item.description,
		time: item.time || item.created_at,
		type: item.type
	}))

	// Format task summary for DonutChart
	const taskChartData = taskSummary ? [
		{ name: 'Pending', value: taskSummary.pending || 0, color: 'var(--color-warning)' },
		{ name: 'In Progress', value: taskSummary.in_progress || 0, color: 'var(--color-primary)' },
		{ name: 'Completed', value: taskSummary.completed || 0, color: 'var(--color-success)' },
		{ name: 'Overdue', value: taskSummary.overdue || 0, color: 'var(--color-danger)' }
	].filter(item => item.value > 0) : []

	// Format projects for ProjectCard
	// Backend returns: target_date, tasks_total, tasks_completed
	// ProjectCard expects: due_date, total_tasks, completed_tasks
	const formattedProjects = (projects || []).slice(0, 4).map(project => ({
		id: project.id,
		name: project.name,
		description: project.description,
		status: project.status,
		approval_status: project.approval_status,
		framework_name: project.framework_name,
		due_date: project.target_date || project.due_date,
		total_tasks: project.tasks_total || 0,
		completed_tasks: project.tasks_completed || 0
	}))

	// Format deadlines for DeadlineList
	const formattedDeadlines = (deadlines || []).map(item => ({
		id: item.id,
		title: item.title,
		date: item.due_date,
		priority: item.urgency === 'overdue' || item.urgency === 'urgent' ? 'high' :
			item.urgency === 'warning' ? 'medium' : 'low',
		daysRemaining: item.days_remaining
	}))

	// Format team performance
	const formattedTeamPerformance = (teamPerformance || []).map(member => ({
		id: member.id,
		name: member.name,
		avatar: member.avatar,
		tasksCompleted: member.tasks_completed || 0,
		onTimePercentage: member.on_time_percentage || 0,
		currentLoad: member.current_load || 0,
		health: member.health || 'good'
	}))

	return (
		<div className="dashboard-page company-dashboard">
			{/* Page Header */}
			<div className="page-header">
				<div className="page-header__content">
					<div>
						<h1 className="page-title">Dashboard</h1>
						<nav className="breadcrumb">
							<span className="breadcrumb__item breadcrumb__item--active">Dashboard</span>
						</nav>
					</div>
					<div className="page-header__actions">
						<span className="last-updated">
							Last updated: {getTimeSinceUpdate()}
							{autoRefreshEnabled && <span className="auto-refresh-badge">Auto</span>}
						</span>
						<button
							className="btn btn--secondary btn--sm"
							onClick={handleRefresh}
							disabled={loading}
						>
							{loading ? 'Refreshing...' : 'Refresh'}
						</button>
					</div>
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className="alert alert--danger">
					<p>{error}</p>
					<button onClick={handleRefresh}>Retry</button>
				</div>
			)}

			{/* Stats Row */}
			<StatsRow>
				<StatCard
					title="Active Projects"
					value={stats?.active_projects ?? '-'}
					subtitle={stats?.projects_trend || 'Loading...'}
					trend={stats?.projects_change || 0}
					trendUp={stats?.projects_change > 0}
					icon="📁"
					variant="primary"
					loading={loading}
					onClick={() => navigate('/projects')}
				/>
				<StatCard
					title="Pending Tasks"
					value={taskSummary?.pending ?? '-'}
					subtitle={`${taskSummary?.high_priority || 0} high priority`}
					icon="📋"
					variant="warning"
					loading={loading}
					onClick={() => navigate('/tasks?status=pending')}
				/>
				<StatCard
					title="Compliance Score"
					value={stats?.compliance_score ? `${stats.compliance_score}%` : '-'}
					subtitle={stats?.compliance_score >= 80 ? 'Above average' : 'Needs attention'}
					icon="📊"
					variant={stats?.compliance_score >= 80 ? 'success' : 'warning'}
					loading={loading}
				/>
				<StatCard
					title="Overdue Tasks"
					value={taskSummary?.overdue ?? '-'}
					subtitle="Needs immediate attention"
					icon="⚠️"
					variant="danger"
					loading={loading}
					onClick={() => navigate('/tasks?status=overdue')}
				/>
			</StatsRow>

			{/* Projects and Task Summary Row */}
			<DashboardGrid columns={2}>
				{/* Projects Overview */}
				{canManageProjects && (
					<WidgetContainer
						title="Projects Overview"
						onAction={() => navigate('/projects')}
						actionLabel="View All"
						loading={loading}
					>
						<div className="projects-grid">
							{formattedProjects.length === 0 ? (
								<div className="empty-state">
									<p>No active projects</p>
									<button
										className="btn btn--primary btn--sm"
										onClick={() => navigate('/projects/new')}
									>
										Create Project
									</button>
								</div>
							) : (
								formattedProjects.map(project => (
									<ProjectCard
										key={project.id}
										project={project}
										onClick={() => navigate(`/projects/${project.id}`)}
									/>
								))
							)}
						</div>
					</WidgetContainer>
				)}

				{/* Task Summary */}
				<WidgetContainer
					title="Task Summary"
					onAction={() => navigate('/tasks')}
					actionLabel="View Tasks"
					loading={loading}
				>
					<div className="task-summary-container">
						<div className="task-stats">
							<div className="task-stat">
								<span className="task-stat__value">{taskSummary?.pending || 0}</span>
								<span className="task-stat__label">Pending</span>
							</div>
							<div className="task-stat">
								<span className="task-stat__value">{taskSummary?.in_progress || 0}</span>
								<span className="task-stat__label">In Progress</span>
							</div>
							<div className="task-stat">
								<span className="task-stat__value">{taskSummary?.completed || 0}</span>
								<span className="task-stat__label">Completed</span>
							</div>
							<div className="task-stat task-stat--danger">
								<span className="task-stat__value">{taskSummary?.overdue || 0}</span>
								<span className="task-stat__label">Overdue</span>
							</div>
						</div>
						<DonutChart
							data={taskChartData}
							dataKey="value"
							nameKey="name"
							height={200}
							showLegend={false}
							innerRadius={50}
							outerRadius={80}
							loading={loading}
						/>
					</div>
				</WidgetContainer>
			</DashboardGrid>

			{/* Activity and Deadlines Row */}
			<DashboardGrid columns={2}>
				{/* Recent Activity */}
				<WidgetContainer
					title="Recent Activity"
					loading={loading}
				>
					<ActivityFeed
						activities={formattedActivity}
						loading={loading}
						onViewAll={() => navigate('/activity')}
					/>
				</WidgetContainer>

				{/* Upcoming Deadlines */}
				<WidgetContainer
					title="Upcoming Deadlines"
					onAction={() => navigate('/calendar')}
					actionLabel="View Calendar"
					loading={loading}
				>
					<DeadlineList
						deadlines={formattedDeadlines}
						loading={loading}
						onViewCalendar={() => navigate('/calendar')}
					/>
				</WidgetContainer>
			</DashboardGrid>

			{/* Team Performance - Only visible to admins/managers */}
			{canViewTeamPerformance && (
				<WidgetContainer
					title="Team Performance"
					onAction={() => navigate('/team')}
					actionLabel="View Team"
					loading={loading}
					fullWidth
				>
					<TeamPerformance
						members={formattedTeamPerformance}
						loading={loading}
					/>
				</WidgetContainer>
			)}

			{/* Quick Actions */}
			<QuickActions actions={quickActions} />
		</div>
	)
}

export default Dashboard
